import { Fragment, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { usePart } from "#modules/parts/view/usePart";
import {
  getDefaultValues,
  isAddedValueProcessStep,
} from "#modules/quoteRequestLines/getDefaultValues";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { useQuoteRequestLine } from "#modules/quoteRequestLines/view/useQuoteRequestLine";
import { useQuoteRequestLineCustomInputs } from "#modules/quoteRequestLines/view/useQuoteRequestLineCustomInputs";
import { useSurfaceTreatments } from "#modules/quoteRequestLines/view/useSurfaceTreatments";
import { DetailsPanel } from "#modules/quoteRequestLines/views/DetailsPanel/DetailsPanel";
import { useCustomInputs } from "#modules/quoteRequestLines/views/useCustomInputs";
import { Button } from "#shared/view/designSystem/Button";

export const DetailedManufaturingRangeButton = ({
  quoteRequestLineId,
}: {
  quoteRequestLineId: string | undefined;
}) => {
  const { t } = useTranslation();

  const { quoteRequestLine } = useQuoteRequestLine({
    id: quoteRequestLineId,
  });

  const { customInputs } = useCustomInputs();

  const { customInputs: customInputsValues } = useQuoteRequestLineCustomInputs(quoteRequestLineId);
  const { surfaceTreatments } = useSurfaceTreatments();

  const defaultValues = {
    ...getDefaultValues({
      quoteRequestLine,
      customInputsValues,
      customInputs,
      surfaceTreatments,
    }),
    isDetailsPanelDisplayable: true,
  };

  const processSteps = quoteRequestLine?.processSteps.filter(isAddedValueProcessStep) ?? [];

  const formMethods = useForm<QuoteRequestLineFormData>({
    values: defaultValues,
    resetOptions: {
      keepDirtyValues: false,
    },
  });

  const { part } = usePart(quoteRequestLine?.partId, {
    enabled: !!quoteRequestLine?.partId,
    initialData: quoteRequestLine?.part ?? undefined,
  });

  const [activePanelId, setActivePanelId] = useState<string | null>(null);
  const sidePanel = {
    activePanelId,
    open: (id: string) => setActivePanelId(id),
    close: () => setActivePanelId(null),
  };

  const onCloseSidePanel = () => {
    sidePanel.close();
    formMethods.reset();
  };

  const isSidePanelOpen = sidePanel.activePanelId === quoteRequestLineId;

  if (!quoteRequestLine) {
    return null;
  }

  return (
    <Fragment>
      <FormProvider {...formMethods}>
        <Button
          onClick={() => {
            if (quoteRequestLineId) {
              sidePanel.open(quoteRequestLineId);
            }
          }}
          variant="outline"
          flex={1}
        >
          {t("similarPart.clustering.viewDetailedRange")}
        </Button>

        <DetailsPanel
          onClosePanel={onCloseSidePanel}
          sidePanelOpen={isSidePanelOpen}
          part={part}
          quoteRequestLine={quoteRequestLine}
          assembliesInBatch={[]}
          assemblyChildren={[]}
          onAssemblyActivatedForBatch={sidePanel.open}
          hasChildren={false}
          isChild={false}
          processSteps={processSteps}
        />
      </FormProvider>
    </Fragment>
  );
};
